import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

/**
 * Offline reads: persist the React Query cache to IndexedDB so a reload with no
 * (or slow) connection shows the last good data instantly instead of blank
 * skeletons, then revalidates when the network returns.
 *
 * Custom on purpose: only `@tanstack/react-query` is a dependency, and `dehydrate`
 * / `hydrate` from its core are enough — no extra persist package.
 *
 * Everything successful is persisted. (Check-in and calendar were once excluded
 * because they carried encrypted biometrics; those were removed, so the whole
 * cache — including those views — now survives a reload offline.)
 */

const DB_NAME = 'respiria-cache';
const STORE = 'kv';
const KEY = 'react-query';
/** Don't restore a cache older than this; better to refetch than show stale-forever. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Debounce writes so a burst of cache updates persists once. */
const WRITE_DEBOUNCE_MS = 1500;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Drop the persisted cache. Used when switching accounts (admin impersonation):
 * a reload must not rehydrate the previous user's data. Best-effort.
 */
export async function clearPersistedCache(): Promise<void> {
  try {
    await idbDelete(KEY);
  } catch {
    // Best-effort: if we can't clear it, the ?switched boot path still refetches.
  }
}

/** Load the saved cache into the client, if recent. Best-effort: never throws. */
export async function restoreCache(client: QueryClient): Promise<void> {
  try {
    const saved = await idbGet<{ at: number; state: unknown }>(KEY);
    if (!saved || Date.now() - saved.at > MAX_AGE_MS) return;
    hydrate(client, saved.state);
  } catch {
    // A corrupt or unavailable cache must never stop the app from starting.
  }
}

/** Persist the cache on change (debounced), minus the excluded queries. */
export function startCachePersistence(client: QueryClient): void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const save = () => {
    try {
      const state = dehydrate(client, {
        shouldDehydrateQuery: (query) => query.state.status === 'success',
      });
      void idbSet(KEY, { at: Date.now(), state });
    } catch {
      // Ignore: persistence is an optimisation, not a requirement.
    }
  };

  client.getQueryCache().subscribe(() => {
    clearTimeout(timer);
    timer = setTimeout(save, WRITE_DEBOUNCE_MS);
  });
}
